import { CreatorCollaborator, CreatorsListRole, KeyRole } from 'lib/types';
import { CreatorsListUser } from 'lib/types/users';
import {
  Avatar as Icon,
  Chip,
} from '@mui/material';
import {
  Favorite, Visibility, Info, People,
} from '@mui/icons-material';
import {
  abbreviateNum, postingAccountToUrl, userIsAdmin, userIsPro,
} from 'lib/helpers';
import { Avatar } from 'components/Avatar';
import { imageAvatarUrl } from 'lib/utils/imageUtils';
import { locationToSlug, modelToSlug } from 'lib/utils/modelSlugUtils';
import { ProfileLink } from 'components/ProfileLink';
import { SaveCreatorButton } from 'components/SaveToList';
import { SocialMediaRow } from 'components/SocialMediaRow';
import { CollabAvatar } from 'components/CollabAvatar';
import { MailButton } from 'components/MailButton';
import { CreatorCardClickContext } from 'lib/types/events';
import Link from 'next/link';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { MobileTooltip } from 'components/MobileTooltip';
import { POSTING_ACCOUNT_TYPE } from 'lib/constants';

interface Props {
  user: CreatorsListUser;
  showLinks?: boolean;
  showPro?: boolean;
  hideSave?: boolean;
  context?: CreatorCardClickContext;
  onListsUpdated?: () => void;
}

export const CreatorInfo = ({
  user,
  showLinks = false,
  showPro = false,
  hideSave = false,
  context,
  onListsUpdated,
}: Props) => {
  const currentUser = useCurrentUser();

  const getUserRoles = () => {
    if (!user?.roles?.length && !!user.defaultRole) {
      return (
        <Chip
          clickable={false}
          label={user.defaultRole.name}
        />
      );
    }

    return user.roles
      .filter((r: CreatorsListRole) => user.hiddenRoles
        .every((hr: KeyRole) => hr.roleId !== r.id))
      .slice(0, 3).map((r: CreatorsListRole) => (
        <ProfileLink
          key={r.id}
          user={user}
          query={{ role: modelToSlug(r, 'name') }}
          disabled={!showLinks || !user.termsAcknowledged}
          context={context}
        >

          <Chip
            clickable={showLinks && user.termsAcknowledged}
            avatar={r?.roleCount ? <Icon className="!bg-white !text-black dark:!text-black">{abbreviateNum(r?.roleCount || 0, 0)}</Icon> : undefined}
            label={r.name}
          />
        </ProfileLink>
      ));
  };

  const getCollaborators = () => user.collaborators
    .slice(0, 5).map((c: CreatorCollaborator) => (
      <CollabAvatar
        key={c.id}
        collab={c}
        username={c.username}
        disabled={!showLinks}
      />
    ));

  const getStats = () => {
    const totalFollowers = getTotalFollowers();
    const hasAnyStats = user?.currentLikes || user?.currentCombinedViews || totalFollowers > 0;

    if (!hasAnyStats) {
      return null;
    }

    return (
      <div className="flex gap-2">
        {user?.currentLikes > 0 && (
          <MobileTooltip title={`${user.currentLikes.toLocaleString()} likes`} arrow placement="top">
            <div className="inline-flex items-center">
              <Favorite fontSize="small" />
              <span className="ml-1">{abbreviateNum(user.currentLikes, 0)}</span>
            </div>
          </MobileTooltip>
        )}
        {user.currentCombinedViews > 0
          && (
            <MobileTooltip title={`${user.currentCombinedViews.toLocaleString()} views`} arrow placement="top">
              <div className="inline-flex items-center">
                <Visibility fontSize="small" />
                <span className="ml-1">{abbreviateNum(user.currentCombinedViews, 0)}</span>
              </div>
            </MobileTooltip>
          )}
        {totalFollowers > 0 && (
          <MobileTooltip title={`${totalFollowers.toLocaleString()} followers`} arrow placement="top">
            <div className="inline-flex items-center">
              <People fontSize="small" />
              <span className="ml-1">{abbreviateNum(totalFollowers, 0)}</span>
            </div>
          </MobileTooltip>
        )}
      </div>
    );
  };

  const getTotalFollowers = () => {
    const ownerAccounts = user.postingAccounts?.filter(
      (pa) => pa.type === POSTING_ACCOUNT_TYPE.owner,
    );

    if (!ownerAccounts?.length) {
      return 0;
    }

    return ownerAccounts.reduce((total, pa) => {
      const followersCount = pa.networkAccount?.followersCount || 0;
      return total + followersCount;
    }, 0);
  };

  const renderSocialMedia = () => {
    if (!showLinks) {
      return null;
    }

    const matchingAccount = currentUser?.managedAccounts?.find(
      (acc) => acc.accountId === user.id,
    );

    const currentUserCanAddPostingAccount = userIsAdmin(currentUser)
      || (currentUser?.hasPaidTeam && !!matchingAccount);

    const accountsToRender = user?.postingAccounts
      ?.filter((pa) => pa.type !== POSTING_ACCOUNT_TYPE.manager)
      ?.filter((pa) => pa.showOnProfile)
      ?.filter((pa) => !!postingAccountToUrl(pa.network, pa.account));

    const hasMailButton = !!user?.publicSettings?.contactAllowed;
    const hasPersonalUrl = !!user.personalUrl;

    if (!accountsToRender?.length
      && !hasPersonalUrl
      && !hasMailButton
      && !currentUserCanAddPostingAccount
    ) {
      return null;
    }

    return (
      <div className="flex flex-col gap-1">
        <div className="text-xs font-bold text-mediumgray dark:text-hintgray">
          SOCIALS
        </div>
        <div className="flex gap-1 flex-wrap justify-start w-full">
          <SocialMediaRow profileUser={user} />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-wrap w-full group">
      <div className="flex w-full md:w-2/3 gap-4">
        <div className="flex gap-2 w-1/3 lg:w-1/4">
          <div className="text-orange flex flex-col gap-2">
            {!hideSave
              && (
                <SaveCreatorButton
                  userId={user.id}
                  savedToListsWithStages={user.savedToListsWithStages || []}
                  savedToProjectsWithStages={user.savedToProjectsWithStages || []}
                  onListsUpdated={onListsUpdated}
                />
              )}
            {user?.publicSettings?.contactAllowed && !hideSave
              && <MailButton user={user} />}
          </div>
          <ProfileLink
            user={user}
            disabled={!showLinks}
            linkToExplore={user.isHidden || !user.username}
            context={context}
          >
            <Avatar
              src={imageAvatarUrl(user.avatarUrl, 160)}
              userId={user.id}
              isPro={showPro && userIsPro(user)}
              alt={user.name}
              width={140}
              height={140}
            />
          </ProfileLink>
        </div>
        <div className="w-2/3 lg:w-3/4">
          <div className="flex flex-col">
            <div className="flex gap-2 items-center">
              <h6 className="font-bold text-xl">
                <ProfileLink
                  user={user}
                  disabled={!showLinks}
                  linkToExplore={user.isHidden}
                  context={context}
                >
                  {user.name}
                </ProfileLink>
              </h6>
              <h6 className="text-sm text-mediumgray dark:text-hintgray">
                @
                {user.username}
              </h6>
              {userIsAdmin(currentUser) && user.searchExplanation && (
                <MobileTooltip title={user.searchExplanation} arrow placement="top">
                  <Info fontSize="small" className="text-alpineTeal cursor-help" />
                </MobileTooltip>
              )}
            </div>
            <div className="text-sm text-mediumgray dark:text-hintgray">
              {user.location?.name && (
                <Link href={`/creators?geo=${locationToSlug(user.location)}`}>
                  {user.location.name}
                </Link>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-2">
              <div className="flex flex-wrap">
                {getStats()}
              </div>
              <div className="flex gap-1 flex-wrap">
                {getUserRoles()}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full md:w-1/3 py-2 md:py-0">
        <div className="flex flex-col gap-4">
          {renderSocialMedia()}
          {user.collaborators.length !== 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-xs font-bold text-mediumgray dark:text-hintgray">
                COLLABS
              </div>
              <div className="flex gap-1 flex-wrap">
                {getCollaborators()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
